import * as Sentry from '@sentry/react';

import {
  getSentryDsn,
  getSentryEnvironment,
  getSentryReplaysErrorSampleRate,
  getSentryReplaysSessionSampleRate,
  getSentryTracesSampleRate,
  isDevelopment,
} from '../config';

// Initialize Sentry. `app` separates customer and admin events sharing one
// DSN/environment.
export function initSentry({ app }: { app: 'customer' | 'admin' }): void {
  const sentryDsn = getSentryDsn();
  const environment = getSentryEnvironment();

  if (sentryDsn) {
    const tracesSampleRate = getSentryTracesSampleRate();
    const replaysSessionSampleRate = getSentryReplaysSessionSampleRate();
    const replaysOnErrorSampleRate = getSentryReplaysErrorSampleRate();

    Sentry.init({
      dsn: sentryDsn,
      environment,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Mask text in all non-dev builds regardless of VITE_SENTRY_ENVIRONMENT,
          // so masking fails safe to "on" even if the env var is missing in production.
          maskAllText: isDevelopment() ? false : true,
          // Block media to prevent recording sensitive audio/video
          blockAllMedia: true,
        }),
      ],
      // Performance Monitoring - 100% sampling by default
      tracesSampleRate,
      // Session Replay - conservative default (10%); raise via env var for debugging
      replaysSessionSampleRate,
      replaysOnErrorSampleRate,
    });

    Sentry.setTag('app', app);

    // Surface resolved sample rates as Sentry tags so operators can verify active
    // config from any event in the dashboard without cross-referencing build vars.
    Sentry.setTag('config.sentry.tracesSampleRate', tracesSampleRate);
    Sentry.setTag(
      'config.sentry.replaysSessionSampleRate',
      replaysSessionSampleRate
    );
    Sentry.setTag(
      'config.sentry.replaysOnErrorSampleRate',
      replaysOnErrorSampleRate
    );
  } else if (!isDevelopment()) {
    // A deployed build with no DSN makes every Sentry.captureMessage /
    // captureException in the app a silent no-op — including deliberate
    // tripwires like reportDragIdentityMismatch. The failure mode is that the
    // resulting silence is indistinguishable from "nothing is going wrong", so
    // an inert SDK can sit unnoticed indefinitely. Say so on every load instead.
    console.warn(
      '[ahoy] VITE_SENTRY_DSN is not set for this build — Sentry is inert. ' +
        'No frontend errors, messages, or replays are being captured, and the ' +
        'silence of any Sentry-based detector means nothing.'
    );
  }
}
